/**
 * API utility with automatic token refresh and global error handling
 */

let isRefreshing = false;
let failedQueue = [];
let onLogoutCallback = null;

// Set a callback to be called after logout (for updating React state)
export const setOnLogoutCallback = (callback) => {
  onLogoutCallback = callback;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Enhanced fetch with automatic token refresh
 */
export const apiFetch = async (url, options = {}) => {
  // CRITICAL: Always get fresh token from localStorage
  const token = localStorage.getItem('accessToken');
  
  // Ensure headers object exists
  if (!options.headers) {
    options.headers = {};
  }
  
  // Add Authorization header if token exists
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, options);

  // Check for sliding session token refresh
  const newToken = response.headers.get('X-New-Token');
  if (newToken) {
    localStorage.setItem('accessToken', newToken);
  }

  // If 401 and we have a refresh token, try to refresh
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      // No refresh token - force logout
      handleLogout();
      throw new Error('Session expired. Please login again.');
    }

    if (isRefreshing) {
      // Already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        options.headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(url, options);
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await fetch('/api/auth/token/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const refreshData = await refreshResponse.json();

      if (refreshData.success && refreshData.data.accessToken) {
        const newToken = refreshData.data.accessToken;
        localStorage.setItem('accessToken', newToken);
        
        // Process queued requests
        processQueue(null, newToken);
        
        // Retry original request with new token
        options.headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, options);
      } else {
        // Refresh failed - force logout
        processQueue(new Error('Token refresh failed'), null);
        handleLogout();
        throw new Error('Session expired. Please login again.');
      }
    } catch (error) {
      processQueue(error, null);
      handleLogout();
      throw error;
    } finally {
      isRefreshing = false;
    }
  }

  return response;
};

/**
 * Centralized logout function - handles both frontend and backend cleanup
 */
export const logout = async () => {
  const accessToken = localStorage.getItem('accessToken');
  
  // Call backend logout API to clear refresh token
  if (accessToken) {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
    } catch (error) {
      // Ignore errors - user will be logged out locally anyway
      console.log('Backend logout API call failed (ignorable):', error.message);
    }
  }
  
  // Clear local storage
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  // Call the registered callback (to update React state in AuthContext)
  if (onLogoutCallback) {
    onLogoutCallback();
  }
  
  // Redirect to home
  window.location.href = '/';
};

/**
 * Internal logout handler (called on session expiry)
 */
const handleLogout = async () => {
  await logout();
};

/**
 * Convenience methods
 */
export const api = {
  get: (url, options = {}) => apiFetch(url, { 
    ...options, 
    method: 'GET',
    headers: {
      ...options.headers
    }
  }),
  
  post: (url, data, options = {}) => {
    // Check if data is FormData (for multipart file uploads)
    const isFormData = data instanceof FormData;
    
    return apiFetch(url, {
      ...options,
      method: 'POST',
      headers: isFormData 
        ? { ...options.headers } // Don't set Content-Type for FormData - let browser set it with boundary
        : {
            'Content-Type': 'application/json',
            ...options.headers
          },
      body: isFormData ? data : JSON.stringify(data)
    });
  },
  
  put: (url, data, options = {}) => {
    // Check if data is FormData (for multipart file uploads)
    const isFormData = data instanceof FormData;
    
    return apiFetch(url, {
      ...options,
      method: 'PUT',
      headers: isFormData 
        ? { ...options.headers } // Don't set Content-Type for FormData - let browser set it with boundary
        : {
            'Content-Type': 'application/json',
            ...options.headers
          },
      body: isFormData ? data : JSON.stringify(data)
    });
  },
  
  patch: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    body: JSON.stringify(data)
  }),
  
  delete: (url, options = {}) => apiFetch(url, { ...options, method: 'DELETE' })
};

export default api;
